import { useRef } from 'react';
import camelCase from 'lodash/camelCase';

/**
 * Example usage
 * const domRef = useSetDOMAttributes({
      ref: domInternalRef,
      datasets: {
        testid: 'someDOMActionButton',
      },
      refName: 'someDOMActionButton',
    });
 */

type Params = {
  ref: React.RefObject<HTMLElement>;
  attributes?: Record<string, string>;
  datasets?: Record<string, string>;
  refName?: string;
};

function useSetDOMAttributes(params: Params) {
  const { ref, attributes = {}, datasets = {}, refName = 'dom' } = params;

  const functionalRefName = `${refName}Ref`;

  const result = useRef({
    [functionalRefName](DOMNode: HTMLElement | null) {
      if (!DOMNode) {
        return;
      }

      // Start adding the attributes
      Object.entries(attributes).forEach((attributeEntry) =>
        DOMNode.setAttribute(...attributeEntry),
      );
      // Data attributes are camelized however it will be as it is defined
      // In html element.
      // Refer: https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/dataset
      Object.entries(datasets).forEach(([datasetKey, datasetValue]) => {
        DOMNode.dataset[camelCase(datasetKey)] = datasetValue;
      });

      // @ts-expect-error: Since we are using a ref object, we can safely assign to it
      ref.current = DOMNode;
    },
  });

  return result.current?.[functionalRefName];
}

export default useSetDOMAttributes;
